import { useQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { toast } from "sonner";
import type {
  Tables,
  TablesInsert,
  TablesUpdate,
} from "@/integrations/neon/types";
import { generateUUID } from "@/lib/utils";
import {
  reportServerMutationFailure,
  toLegacyTimestamp,
  usePendingMutation,
  type MutationOptions,
  type ZeroMutateClient,
} from "./useZeroMutationUtils";

export type ProjectAwsResourceLink = Tables<"project_aws_resource_links">;
export type ProjectAwsResourceLinkInsert =
  TablesInsert<"project_aws_resource_links">;
export type ProjectAwsResourceLinkUpdate =
  TablesUpdate<"project_aws_resource_links">;

type ZeroProjectAwsResourceLink = {
  id: string;
  projectID: string;
  resourceType: ProjectAwsResourceLink["resource_type"];
  resourceID?: string | null;
  resourceArn?: string | null;
  resourceName?: string | null;
  metadata?: ProjectAwsResourceLink["metadata"];
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
};
type ProjectAwsResourceLinkMutators = {
  projectAwsResourceLinks: {
    create: (input: ReturnType<typeof toZeroLinkInput>) => unknown;
    update: (input: ReturnType<typeof toZeroLinkUpdate>) => unknown;
    delete: (input: { id: string; projectID: string }) => unknown;
  };
};

function toLegacyLink(row: ZeroProjectAwsResourceLink): ProjectAwsResourceLink {
  return {
    id: row.id,
    project_id: row.projectID,
    resource_type: row.resourceType,
    resource_id: row.resourceID ?? null,
    resource_arn: row.resourceArn ?? null,
    resource_name: row.resourceName ?? null,
    metadata: row.metadata ?? {},
    created_at: toLegacyTimestamp(row.createdAt) ?? "",
    updated_at: toLegacyTimestamp(row.updatedAt) ?? "",
  };
}

function toZeroLinkInput(link: ProjectAwsResourceLinkInsert) {
  return {
    id: link.id ?? generateUUID(),
    projectID: link.project_id,
    resourceType: link.resource_type,
    resourceID: link.resource_id ?? null,
    resourceArn: link.resource_arn ?? null,
    resourceName: link.resource_name ?? null,
    metadata: link.metadata ?? {},
  };
}

function toZeroLinkUpdate(
  id: string,
  projectID: string,
  update: ProjectAwsResourceLinkUpdate,
) {
  return {
    id,
    projectID,
    ...(update.resource_id !== undefined
      ? { resourceID: update.resource_id }
      : {}),
    ...(update.resource_arn !== undefined
      ? { resourceArn: update.resource_arn }
      : {}),
    ...(update.resource_name !== undefined
      ? { resourceName: update.resource_name }
      : {}),
    ...(update.metadata !== undefined ? { metadata: update.metadata } : {}),
  };
}

export function useProjectAwsResourceLinks(projectId: string | undefined) {
  const query = projectId
    ? appQueries.projectAwsResourceLinks.byProject({
        projectID: projectId,
      })
    : undefined;
  const [data, details] = useQuery(query, { enabled: !!projectId });
  const links = ((data ?? []) as ZeroProjectAwsResourceLink[]).map(
    toLegacyLink,
  );
  return {
    data: links,
    isLoading: !!projectId && details.type !== "complete" && links.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(
            typeof details.error === "string"
              ? details.error
              : details.error.message,
          )
        : null,
  };
}

export function useAllProjectAwsResourceLinks() {
  const query = appQueries.projectAwsResourceLinks.list({});
  const [data, details] = useQuery(query);
  const links = ((data ?? []) as ZeroProjectAwsResourceLink[]).map(
    toLegacyLink,
  );
  return {
    data: links,
    isLoading: details.type !== "complete" && links.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(
            typeof details.error === "string"
              ? details.error
              : details.error.message,
          )
        : null,
  };
}

export function useCreateProjectAwsResourceLink() {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  const run = (link: ProjectAwsResourceLinkInsert) =>
    runPending(async () => {
      const input = toZeroLinkInput(link);
      const mutation = zero.mutate(
        (
          appMutators as ProjectAwsResourceLinkMutators
        ).projectAwsResourceLinks.create(input),
      );
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Project AWS resource link create was rejected",
      );
      return toLegacyLink(input);
    });

  return {
    mutate: (
      link: ProjectAwsResourceLinkInsert,
      options?: MutationOptions<ProjectAwsResourceLink>,
    ) => {
      void run(link)
        .then((data) => {
          toast.success("AWS resource linked");
          options?.onSuccess?.(data);
        })
        .catch((err) =>
          toast.error("Failed to link AWS resource: " + err.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useUpdateProjectAwsResourceLink() {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  const run = ({
    id,
    projectId,
    update,
  }: {
    id: string;
    projectId: string;
    update: ProjectAwsResourceLinkUpdate;
  }) =>
    runPending(async () => {
      const input = toZeroLinkUpdate(id, projectId, update);
      const mutation = zero.mutate(
        (
          appMutators as ProjectAwsResourceLinkMutators
        ).projectAwsResourceLinks.update(input),
      );
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Project AWS resource link update was rejected",
      );
      return { id, projectId };
    });

  return {
    mutate: (
      input: {
        id: string;
        projectId: string;
        update: ProjectAwsResourceLinkUpdate;
      },
      options?: MutationOptions<{ id: string; projectId: string }>,
    ) => {
      void run(input)
        .then((result) => {
          toast.success("AWS resource link updated");
          options?.onSuccess?.(result);
        })
        .catch((err) =>
          toast.error("Failed to update AWS resource link: " + err.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useDeleteProjectAwsResourceLink() {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  const run = ({ id, projectId }: { id: string; projectId: string }) =>
    runPending(async () => {
      const mutation = zero.mutate(
        (
          appMutators as ProjectAwsResourceLinkMutators
        ).projectAwsResourceLinks.delete({
          id,
          projectID: projectId,
        }),
      );
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Project AWS resource link delete was rejected",
      );
      return { projectId };
    });

  return {
    mutate: (
      input: { id: string; projectId: string },
      options?: MutationOptions<{ projectId: string }>,
    ) => {
      void run(input)
        .then((result) => {
          toast.success("AWS resource unlinked");
          options?.onSuccess?.(result);
        })
        .catch((err) =>
          toast.error("Failed to unlink AWS resource: " + err.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}
